import {Component, Inject, OnInit, ViewChild, ViewChildren} from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import {LayoutService} from '../home/layout.service';
import {durationToStr, User} from 'shared';
import {ReconstructedInterventionService, ReconstructedIntervention} from '../stores/reconstructed-intervention.service';
import {durationToHoursStr} from 'shared';
import CustomStore from 'devextreme/data/custom_store';
import {DxDataGridComponent} from 'devextreme-angular';
import {InterventionsRequest} from './interventions-toolbar/interventions-toolbar.component';
import {HttpClient} from '@angular/common/http';
import {notify, ConfigService} from 'shared';
import moment from 'moment';
import reportIcon from '@iconify/icons-mdi/chart-areaspline';
import checkIcon from '@iconify/icons-mdi/check-circle';
import closeIcon from '@iconify/icons-mdi/close-circle';
// editIcon removed - edit functionality no longer needed
import {GoldenLayoutContainer} from 'ngx-golden-layout';
import GoldenLayout from 'golden-layout';

@Component({
  selector: 'app-reconstructed-interventions-grid',
  templateUrl: './reconstructed-interventions-grid.component.html',
  styleUrls: ['./reconstructed-interventions-grid.component.scss']
})
export class ReconstructedInterventionsGridComponent implements OnInit {
  ds: DataSource;
  canEdit: boolean = false;
  canAdd: boolean = false;
  canDelete: boolean = false;
  canAssign: boolean = false;
  canView: boolean = false;
  canGenerate: boolean = false;
  canValidate: boolean = false;
  canReject: boolean = false;
  canViewReport: boolean = false;
  expandedAlertRowKey = 0;
  request: InterventionsRequest = new InterventionsRequest();
  dateFormat = 'shortDateShortTime';
  selectedIntervention: ReconstructedIntervention = null;
  reportVisible: boolean = false;
  reportIcon = reportIcon;
  checkIcon = checkIcon;
  closeIcon = closeIcon;
  // editIcon removed - edit functionality no longer needed
  reconstructDate: Date = moment().subtract(1, 'days').toDate();

  // Helper methods to create colored SVG icons
  getValidateIcon(): string {
    const svgBody = checkIcon.body.replace(/fill="[^"]*"/g, 'fill="#28a745"');
    return `<svg width="18px" height="18px" viewBox="0 0 24 24">${svgBody}</svg>`;
  }

  getRejectIcon(): string {
    const svgBody = closeIcon.body.replace(/fill="[^"]*"/g, 'fill="#dc3545"');
    return `<svg width="18px" height="18px" viewBox="0 0 24 24">${svgBody}</svg>`;
  }

  // getEditIcon removed - edit functionality no longer needed

  getDetailsIcon(): string {
    const svgBody = reportIcon.body.replace(/fill="[^"]*"/g, 'fill="#ffc107"');
    return `<svg width="18px" height="18px" viewBox="0 0 24 24">${svgBody}</svg>`;
  }

  @ViewChild(DxDataGridComponent, {static: true})
  grid: DxDataGridComponent;
  @ViewChild('validateDialog') validateDialog: any;
  @ViewChild('rejectDialog') rejectDialog: any;
  gridCss: { width: string; height: string } = { width: '100%', height: '100%' };

  constructor(
    private reconstructedInterventionService: ReconstructedInterventionService,
    @Inject(GoldenLayoutContainer) private container: GoldenLayout.Container,
    private layoutService: LayoutService,
    public user: User,
    private http: HttpClient,
    private config: ConfigService,
  ) {

    // Bindings
    this.showInterventionDetails = this.showInterventionDetails.bind(this);
    this.reconstruct = this.reconstruct.bind(this);

    // Set grid dimensions in constructor (like other grid components)
    if (this.container && this.container.getElement()) {
      this.gridCss = {
        width: this.container.getElement().width() + 'px',
        height: this.container.getElement().height() - 51 + 'px',
      };
    }
  }

  ngOnInit(): void {
    // Permissions - set in ngOnInit to avoid change detection issues
    this.canEdit = this.user.can('edit trackers');
    this.canView = this.user.can('list reconstructed_interventions');
    this.canGenerate = this.user.can('generate reconstructed_interventions');
    this.canValidate = this.user.can('validate reconstructed_interventions');
    this.canReject = this.user.can('reject reconstructed_interventions');
    this.canViewReport = this.user.can('view reconstructed_intervention_reports');
  }

  hectareUnit(cellInfo) {
    return (parseFloat(cellInfo.value) / 10000).toFixed(2) + ' Ha';
  }

  widthUnit(cellInfo) {
    return (parseInt(cellInfo.value, 10) / 100).toFixed(1) + ' m';
  }

  percent(cellInfo) {
    if (!cellInfo.value) {
      return '-';
    }
    return (parseFloat(cellInfo.value) * 100).toFixed(2) + ' %';
  }

  duration(cellInfo) {
    return `<span style="text-decoration-style: dashed;" title="${durationToStr(cellInfo.value)}">${durationToHoursStr(cellInfo.value)} h</span>`;
  }

  durationToHoursStr(cellInfo) {
    return durationToHoursStr(cellInfo.value);
  }

  durationToStr(cellInfo) {
    return durationToStr(cellInfo.value);
  }

  getAccuracyRatingColor(rating: number): string {
    if (!rating) return 'gray';
    if (rating >= 8) return 'green';
    if (rating >= 6) return 'yellow';
    if (rating >= 4) return 'orange';
    return 'red';
  }

  getInterventionTypeLabel(rowData: any): string {
    const type = rowData.intervention_type;
    const types = {
      'SPRAYING': 'Pulvérisation',
      'SEEDING': 'Semis',
      'HARVESTING': 'Récolte',
      'PLOWING': 'Labour',
      'CULTIVATION': 'Cultivation',
      'FERTILIZING': 'Fertilisation',
      'OTHER': 'Autre'
    };
    return types[type] || type;
  }

  load(request: InterventionsRequest) {
    if (!this.ds) {
      // Data source
      this.ds = this.reconstructedInterventionService.newDataSource();
    }

    this.request = request;

    this.dateFormat = this.request.isToday ? 'shortTime' : 'shortDateShortTime';

    this.ds.filter([
      ['start_time', '>=', request.from],
      'and',
      ['start_time', '<=', request.to]
    ]);

    this.ds.reload();
  }

  showInterventionDetails = (e: any) => {
    if (!this.canViewReport) {
      notify('Vous n\'avez pas la permission de voir les rapports d\'interventions', 'error', 3000);
      return;
    }
    const intervention = e.row?.data || e.data;
    this.selectedIntervention = intervention;
    this.reportVisible = true;
  }

  validateIntervention = (e: any) => {
    if (!this.canValidate) {
      notify('Vous n\'avez pas la permission de valider les interventions', 'error', 3000);
      return;
    }
    const intervention = e.row?.data || e.data;
    if (this.validateDialog && intervention?.id) {
      this.validateDialog.show(intervention.id);
    }
  }

  // validateAndCreateFields removed - now handled in validate dialog with toggle

  rejectIntervention = (e: any) => {
    if (!this.canReject) {
      notify('Vous n\'avez pas la permission de rejeter les interventions', 'error', 3000);
      return;
    }
    const intervention = e.row?.data || e.data;
    if (this.rejectDialog && intervention?.id) {
      this.rejectDialog.show(intervention.id);
    }
  }


  isPendingValidation = (e: any) => {
    const intervention = e.row?.data || e.data;
    return intervention?.status === 'pending_validation';
  }

  onValidateSuccess() {
    // Reload grid after successful validation
    if (this.ds) {
      this.ds.reload();
    }
  }

  onRejectSuccess() {
    // Reload grid after successful rejection
    if (this.ds) {
      this.ds.reload();
    }
  }

  onReportActionSuccess() {
    // Reload grid after action from report popup
    if (this.ds) {
      this.ds.reload();
    }
    // Selected intervention will be refreshed when user views the report again
    // since it's loaded from the grid data
  }

  reconstruct() {
    if (!this.canGenerate) {
      notify('Vous n\'avez pas la permission de lancer une reconstruction manuelle', 'error', 3000);
      return;
    }

    if (!this.reconstructDate) {
      notify('Veuillez sélectionner une date', 'error', 3000);
      return;
    }

    const dateStr = moment(this.reconstructDate).format('YYYY-MM-DD');

    // Show loading notification
    notify('Reconstruction en cours...', 'info', 2000);

    // Call reconstruction API using the same URL pattern as other endpoints
    this.http.post(this.config.apiVerUrl('/app/reconstructed-interventions/generate'), {
      date: dateStr
    }).subscribe({
      next: (response: any) => {
        notify(`Reconstruction démarrée pour ${dateStr}`, 'success', 5000);
        // Optionally reload grid after a delay
        setTimeout(() => {
          if (this.ds) {
            this.ds.reload();
          }
        }, 2000);
      },
      error: (error) => {
        const errorMsg = error.error?.error || 'Erreur lors de la reconstruction';
        notify(errorMsg, 'error', 5000);
      }
    });
  }
}

